/**
 * Rank & Pick Editor
 *
 * Configure ranking formula and selection method
 */

import React from 'react';
import type { RankPickSpec, Method, Order, NAPolicy } from '../../types/selectionV2.ts';

interface RankPickEditorProps {
  spec: RankPickSpec;
  onChange: (spec: RankPickSpec) => void;
  formulas?: Array<{ id: string; name: string; formula: string }>;
  onboardingStep?: number;
  onOnboardingComplete?: () => void;
}

const RankPickEditor: React.FC<RankPickEditorProps> = ({
  spec,
  onChange,
  formulas = [],
  onboardingStep = 4,
  onOnboardingComplete
}) => {
  const methods: { value: Method; label: string }[] = [
    { value: 'top_pct', label: 'Top %' },
    { value: 'top_n', label: 'Top N' },
    { value: 'bottom_pct', label: 'Bottom %' },
    { value: 'bottom_n', label: 'Bottom N' }
  ];

  // Find if current formula matches any of the available formulas
  const matchingFormula = formulas.find(f => f.formula === spec.formula);
  const selectedFormulaId = matchingFormula ? matchingFormula.id : '';

  return (
    <div className="space-y-4">
      {/* Formula Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Ranking Formula
        </label>
        <div className="flex gap-2">
          {formulas.length > 0 && (
            <select
              value={selectedFormulaId}
              onChange={(e) => {
                const formula = formulas.find(f => f.id === e.target.value);
                if (formula) {
                  onChange({ ...spec, formula: formula.formula });
                  // Complete onboarding when user selects a formula
                  if (onboardingStep === 3 && onOnboardingComplete) {
                    onOnboardingComplete();
                  }
                }
              }}
              className={`px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                onboardingStep === 3 ? 'onboarding-blink' : ''
              }`}
            >
              <option value="">-- Select Formula --</option>
              {formulas.map(f => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          )}
          <input
            type="text"
            value={spec.formula}
            onChange={(e) => onChange({ ...spec, formula: e.target.value })}
            placeholder="e.g., eps.ttm(0) / price"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
          />
        </div>
      </div>

      {/* Order & Method Row */}
      <div className="grid grid-cols-3 gap-4">
        {/* Order */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Order
          </label>
          <select
            value={spec.order}
            onChange={(e) => onChange({ ...spec, order: e.target.value as Order })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="desc">Descending (high to low)</option>
            <option value="asc">Ascending (low to high)</option>
          </select>
        </div>

        {/* Method */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Selection Method
          </label>
          <select
            value={spec.method}
            onChange={(e) => onChange({ ...spec, method: e.target.value as Method })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {methods.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>

        {/* K value */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {spec.method.includes('pct') ? 'Percent' : 'Count'}
          </label>
          <input
            type="number"
            value={spec.k}
            onChange={(e) => onChange({ ...spec, k: parseFloat(e.target.value) })}
            min="0"
            max={spec.method.includes('pct') ? 100 : undefined}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* NA Policy */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Handle Missing Data
        </label>
        <select
          value={spec.naPolicy || 'drop'}
          onChange={(e) => onChange({ ...spec, naPolicy: e.target.value as NAPolicy })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="drop">Drop NAs (exclude from ranking)</option>
          <option value="worst">Treat NAs as worst (rank at bottom)</option>
        </select>
      </div>
    </div>
  );
};

export default RankPickEditor;
